/**
 * Implement a Hero in Banner form
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import CoverImage from 'client/shared/js/components/CoverImage'
import AnnouncementsBanner, { PRODUCT } from 'client/shared/js/components/AnnouncementsBanner'
import styles from './HeroBanner.module.scss'

const HeroBanner = ({
  className,
  announcements,
  photoUrl,
  backgroundFilter,
  size,
  children,
  cornerStyle,
  fixedHeight
}) => {
  const GlobalAnnouncement = useMemo(() => {
    return announcements?.length > 0 && (
      <div className={cx(styles['global-announcement'])}>
        <AnnouncementsBanner
          announcements={announcements}
          allowedTags={[PRODUCT]} />
      </div>
    )
  }, [announcements])

  return (
    <section className={cx(
      className,
      styles['hero-banner'],
      {
        [styles['corner-rounded']]: cornerStyle === 'rounded'
      }
    )}>
      {GlobalAnnouncement}
      <CoverImage
        className={'hero-image'}
        photoUrl={photoUrl}
        backgroundFilter={backgroundFilter}>
        <div className={cx(styles['hero-content'], 'hero-content', styles[size], {
          [styles['reset-responsive-height']]: !fixedHeight
        })}>
          {children}
        </div>
      </CoverImage>
    </section>
  )
}

HeroBanner.defaultProps = {
  backgroundFilter: 'full',
  size: 'sm',
  cornerStyle: 'rounded',
  fixedHeight: false
}

HeroBanner.propTypes = {
  className: PropTypes.string,
  announcements: PropTypes.array,
  photoUrl: PropTypes.string,
  backgroundFilter: PropTypes.oneOf(['top', 'bottom', 'full', false]),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  fixedHeight: PropTypes.bool,
  children: PropTypes.node,
  cornerStyle: PropTypes.oneOf(['rounded', 'square'])
}

export default HeroBanner
